import { useState, useEffect } from 'react';

import { api } from '../api';
import { getTenantName } from '../account-manager';
import { addTelemetryInitializer, trackEvent } from '../tracking-tool';

interface TenantModel {
    addons: Record<string, any>[];
    api_version: number;
    b2Cpolicies: Record<string, any>[];
    friendly_name: string;
    is_expired: boolean;
    language: string;
    name: string;
    terms_and_conditions: string | null;
    version: number;
    is_process_publication?: false;
}

type UseTenantValue = {
    tenant: Tenant;
    refreshTenant(): Promise<void>;
};

class Tenant {
    addons: Record<string, any>[];
    apiVersion: number;
    b2CPolicies: Record<string, any>[];
    b2CVendor: Record<string, any>;
    friendlyName: string;
    isExpired: boolean;
    language: string;
    name: string;
    termsAndConditions: string | null;
    version: number;

    constructor(data: TenantModel) {
        const b2cPolicies: Record<string, any>[] = [];
        data.b2Cpolicies.forEach((p) => {
            if (!data.is_process_publication) {
                const b2c_instance = p.b2c_instance.instance.split('/{0}')[0];
                b2cPolicies.push({
                    b2cPolicyName: p.b2c_policy_name,
                    b2cPolicy: `${b2c_instance}/${p.b2c_instance.tenant}/${p.b2c_policy_name}`,
                    image: p.image,
                    isVendor: p.is_vendor,
                    id: `${p.b2c_policy_name}_id`,
                    b2cClientId: p.b2c_instance.client_id,
                    b2cInstance: p.b2c_instance.instance,
                    b2cTenant: p.b2c_instance.tenant,
                });
            }
        });
        const nonVendors = b2cPolicies.filter((policy) => !policy.isVendor);
        const vendor = b2cPolicies.find((policy) => policy.isVendor);

        this.b2CPolicies = nonVendors;
        this.b2CVendor = vendor;
        this.name = data.name;
        this.addons = data.addons;
        this.apiVersion = data.api_version;
        this.friendlyName = data.friendly_name;
        this.isExpired = data.is_expired;
        this.language = data.language;
        this.termsAndConditions = data.terms_and_conditions;
        this.version = data.version;
    }
}

let tenantData: Tenant = {} as Tenant;

/**
 * returns the DiagramContext
 * @returns {UseTenantValue}
 */
export function useTenant(): UseTenantValue {
    const [tenant, setTenant] = useState(tenantData);

    /**
     * refresh the tenant data
     */
    async function refreshTenant(): Promise<void> {
        const tenantName = getTenantName();
        if (tenantName) {
            try {
                const { data } = await api.get<TenantModel>({
                    url: '/tenant',
                    headers: {
                        Tenant: tenantName,
                    },
                });
                api.setTenant(tenantName);
                const updatedTenantData = new Tenant(data);
                setTenant(updatedTenantData);
                tenantData = updatedTenantData;
                addTelemetryInitializer(tenantName);
            } catch (error) {
                trackEvent('UTIL_MODULE_XHR_TENANT_ERROR', {
                    error,
                });
            }
        }
    }

    useEffect(() => {
        if (tenantData.name && tenantData.language) {
            setTenant(tenantData);
        } else {
            refreshTenant();
        }
    }, []);

    return { tenant, refreshTenant };
}
